import { useSelector } from "react-redux";
import { DeepPartial } from "redux";
import axios, { AxiosError, AxiosResponse } from "axios";
import { Circuit, VariantName } from "../objects/circuit";
import { AppState } from "../../../reducers";

type Options = {
    onTrigger?: () => void,
    onSuccess?: (circuit: Circuit) => void,
    onError?: (error: AxiosError) => void,
    onFinally?: () => void
}

type VariantNamePayload = Omit<VariantName, 'allowed_cms'> & { allowed_cms?: number[] }

export type CircuitUpdateData = DeepPartial<
    Omit<Circuit, 'destination' | 'linked_destinations' | 'categories' | 'contracts' | 'pictures' | 'map_picture' | 'allowed_cms' | 'allowed_markets' | 'sub_market' | 'variant_name'> &
    {
        destination: number,
        linked_destinations: number[],
        categories: number[],
        contracts: number[],
        pictures: number[],
        map_picture: number | null,
        file_ids: number[],
        banner_pictures: number[],
        allowed_cms: number[],
        allowed_markets: number[],
        sub_market: number,
        variant_name: VariantNamePayload[]
    }
>

export function useCircuitUpdate(options: Options): (
    circuitId: number,
    circuit: CircuitUpdateData
) => Promise<Circuit | null> {
    const clientId = useSelector((state: AppState) => state.header.client_id);

    return async (circuitId, circuit) => {
        if (clientId !== null) {
            try {
                if (options.onTrigger) {
                    options.onTrigger();
                }
                const response = await makeRequest(clientId, circuitId, circuit);
                if (options.onSuccess) {
                    options.onSuccess(response.data);
                }
                return response.data;
            } catch (error: any) {
                if (options.onError) {
                    options.onError(error);
                }
            } finally {
                if (options.onFinally) {
                    options.onFinally();
                }
            }
        }
        return null;
    };
}

function makeRequest(
    clientId: number,
    circuitId: number,
    circuit: CircuitUpdateData
): Promise<AxiosResponse<Circuit>> {
    return axios.patch(
        `${API_HREF}client/${clientId}/circuits/${circuitId}/`,
        circuit,
        {
            headers: {
                'Authorization': `Token ${localStorage.getItem('token')}`,
            },
        }
    );
}